import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Terminal,
  DataObject,
  AccountBox,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode, toggleDarkMode } = useThemeMode();

  const menuItems = ['About', 'Experience', 'Skills', 'Projects', 'Contact'];

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const ThemeSwitcher = ({ isMobile = false }) => {
    const handleThemeToggle = React.useCallback(() => {
      // Add temporary class to reduce transitions during theme change
      document.body.classList.add('theme-switching');
      toggleDarkMode();
      // Remove class after theme change is complete
      setTimeout(() => {
        document.body.classList.remove('theme-switching');
      }, 100);
    }, [toggleDarkMode]);

    return (
      <Box sx={{ position: 'relative' }}>
        <Tooltip title="Change theme">
          <Chip
            icon={<DataObject sx={{ fontSize: 16 }} />}
            label={
              <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' }}>
                {`theme: "${darkMode ? 'dark' : 'light'}"`}
              </Box>
            }
            onClick={handleThemeToggle}
          sx={{
            backgroundColor: darkMode 
              ? 'rgba(100, 181, 246, 0.15)' 
              : 'rgba(25, 118, 210, 0.15)',
            color: 'primary.main',
            border: `1px solid ${theme.palette.primary.main}60`,
            cursor: 'pointer',
            fontSize: '0.75rem',
            height: isMobile ? 32 : 28,
            '&:hover': {
              backgroundColor: darkMode 
                ? 'rgba(100, 181, 246, 0.25)' 
                : 'rgba(25, 118, 210, 0.25)',
              transform: 'scale(1.05)',
              boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </Tooltip>
    </Box>
    );
  };

  const drawer = (
    <Box 
      onClick={handleDrawerToggle} 
      sx={{ 
        textAlign: 'center',
        background: darkMode 
          ? 'linear-gradient(135deg, rgba(10,10,10,0.98) 0%, rgba(26,26,26,0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
        backdropFilter: 'blur(24px)',
        height: '100%',
        borderLeft: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <AccountBox sx={{ mr: 1.5, color: 'primary.main', fontSize: 24 }} />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 800, 
              background: 'linear-gradient(45deg, #1976d2, #dc004e)', 
              backgroundClip: 'text', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              fontSize: '1.1rem',
            }}
          >
            Yogesh More
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ThemeSwitcher isMobile={true} />
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          {`// Frontend Developer`}
        </Typography>
      </Box>
      <List sx={{ pt: 2 }}>
        {menuItems.map((item, index) => (
          <ListItem key={item} disablePadding>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}.
                  </Typography>
                  <Typography variant="body1">{item}</Typography>
                </Box>
              }
              onClick={() => scrollToSection(item)}
              sx={{ 
                textAlign: 'center', 
                cursor: 'pointer',
                py: 1.5,
                mx: 2,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  transform: 'translateX(4px)',
                },
                transition: 'all 0.2s ease',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: scrolled 
            ? darkMode 
              ? 'rgba(10,10,10,0.90)'
              : 'rgba(255,255,255,0.90)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: scrolled 
              ? darkMode
                ? 'linear-gradient(90deg, rgba(100, 181, 246, 0.05) 0%, rgba(244, 143, 177, 0.05) 100%)'
                : 'linear-gradient(90deg, rgba(25, 118, 210, 0.03) 0%, rgba(220, 0, 78, 0.03) 100%)'
              : 'none',
            zIndex: -1,
          },
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBox sx={{ color: 'primary.main', fontSize: 28 }} />
              <Box>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ 
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    background: 'linear-gradient(45deg, #1976d2, #dc004e)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                  }}
                >
                  Yogesh More
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: '"JetBrains Mono", monospace',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {`// Frontend Developer`}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {menuItems.map((item, index) => (
                  <Button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      position: 'relative',
                      fontSize: '0.9rem',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        transform: 'translateY(-1px)',
                      },
                      '&::before': {
                        content: `"${String(index + 1).padStart(2, '0')}."`,
                        position: 'absolute',
                        top: -2,
                        left: 4,
                        fontSize: '0.6rem',
                        fontFamily: '"JetBrains Mono", monospace',
                        color: 'primary.main',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      },
                      '&:hover::before': {
                        opacity: 1,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        width: 0,
                        height: 2,
                        backgroundColor: 'primary.main',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover::after': {
                        width: '80%',
                      },
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
              
              <ThemeSwitcher />
            </Box>
          )}
          
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: { xs: 280, sm: 320 },
            border: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;